import { useCallback, useEffect, useMemo, useState } from 'react';
import { css, cx } from '@linaria/atomic';
import { GlsOrg, WORKSPACE_TAG } from '@wetags/gls';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import {
  LocationGroup,
  LocationGroupSection,
  LocationPickerHeader,
  useLocationPicker,
  LocationSearchGroup,
  LocationSearchResults,
  GroupSearchResults,
  OrgSearchResults,
  useLocationPickerStore,
  LocationGroupSectionSimpleView,
} from '@frontend/location-picker';
import { type WeaveLocation, type WeaveLocationGroup, useSelectedScope } from '@frontend/scope';
import { useWorkspaceShallowStore, useWorkspaceState } from '@frontend/workspace-scope';
import { GlsWorkspaceSection } from '@frontend/workspaces';
import { theme } from '@frontend/theme';
import { Button, Text, emptyStateGraphics, Truncated } from '@frontend/design-system';
import { PrivilegedPickerProvider, usePrivilegedPicker } from './privileged-picker-context';

export interface LocationGroupResult {
  groupId: string;
  groupName: string;
  orgId: string;
  orgName: string;
  locations: Array<{
    id: string;
    name: string;
  }>;
}

interface PrivilegedLocationPickerProps {
  closeModal?: () => void;
  children: React.ReactNode;
  renderOrgInfo?: (orgId: string) => JSX.Element | null;
  renderLocationInfo?: (locationId: string) => JSX.Element | null;
  renderBannerInfo?: () => JSX.Element | null;
  onApplyError?: (error: unknown) => void;
  onClickOrgRadio?: () => void;
  selectedOrgId: string;
  selectedGroupId: string;
  selectedLocationIds: string[];
  selectedScope?: {
    scopeType?: string;
    scopeId?: string;
  };
  scopeHierarchy: Record<string, Record<string, string[]>>;
  accessibleLocationData: Record<string, WeaveLocation>;
  accessibleGroupData: Record<string, WeaveLocationGroup>;
  getScopeName: (scope: string, type?: 'location' | 'group' | 'org') => string;
  onApply?: () => Promise<void>;
}

const hasPendingOrgSwitch = ({
  selectedOrgId,
  organizationViewId,
  inFlightOrganizationId,
}: {
  selectedOrgId: string;
  organizationViewId: string;
  inFlightOrganizationId: string;
}) => {
  if (!selectedOrgId) {
    return false;
  }

  const effectiveOrganizationViewId = organizationViewId || selectedOrgId;
  const effectiveInFlightOrganizationId = inFlightOrganizationId || effectiveOrganizationViewId;

  return effectiveOrganizationViewId !== selectedOrgId || effectiveInFlightOrganizationId !== selectedOrgId;
};

const getSelectedGroupIdsForOrg = ({
  orgScopeHierarchy,
  selectedLocationIds,
}: {
  orgScopeHierarchy: Record<string, string[]>;
  selectedLocationIds: string[];
}) => {
  if (!orgScopeHierarchy || selectedLocationIds.length === 0) {
    return [];
  }

  const selectedLocationSet = new Set(selectedLocationIds);

  return Object.entries(orgScopeHierarchy)
    .filter(([, locationIds]) => locationIds.some((locationId) => selectedLocationSet.has(locationId)))
    .map(([groupId]) => groupId);
};

export const PrivilegedLocationProvider = ({
  closeModal,
  children,
  renderBannerInfo,
  renderLocationInfo,
  renderOrgInfo,
  onApplyError,
  onClickOrgRadio,
  selectedOrgId,
  selectedGroupId,
  selectedLocationIds,
  selectedScope,
  scopeHierarchy,
  accessibleLocationData,
  accessibleGroupData,
  getScopeName,
  onApply,
}: PrivilegedLocationPickerProps) => {
  const locationPickerStore = useLocationPickerStore();
  useEffect(() => {
    if (!onClickOrgRadio) return;

    const unsubscribe = locationPickerStore.subscribe(
      (state) => state.inFlightOrganizationId,
      (_) => {
        onClickOrgRadio();
      }
    );

    return () => {
      unsubscribe();
    };
  }, [onClickOrgRadio]);

  return (
    <PrivilegedPickerProvider
      closeModal={closeModal}
      renderBannerInfo={renderBannerInfo}
      renderLocationInfo={renderLocationInfo}
      renderOrgInfo={renderOrgInfo}
      onApplyError={onApplyError}
      selectedScope={selectedScope}
      selectedOrgId={selectedOrgId}
      selectedGroupId={selectedGroupId}
      selectedLocationIds={selectedLocationIds}
      scopeHierarchy={scopeHierarchy}
      accessibleLocationData={accessibleLocationData}
      accessibleGroupData={accessibleGroupData}
      getScopeName={getScopeName}
      onApply={onApply}
    >
      {children}
    </PrivilegedPickerProvider>
  );
};

const LocationPickerFooter = () => {
  const { inFlightOrganizationId, inFlightLocationIds, isSimpleView } = useLocationPicker([
    'inFlightOrganizationId',
    'inFlightLocationIds',
    'isSimpleView',
  ]);
  const { pendingWorkspaceId, commitPendingWorkspace } = useWorkspaceShallowStore(
    'pendingWorkspaceId',
    'commitPendingWorkspace'
  );
  const [filterLoading, setFilterLoading] = useState(false);
  const closeModal = usePrivilegedPicker((state) => state.closeModal);
  const onApplyError = usePrivilegedPicker((state) => state.onApplyError);
  const hasSwitchedScope = usePrivilegedPicker((state) => state.hasSwitchedScope);
  const scopeHierarchy = usePrivilegedPicker((state) => state.scopeHierarchy);
  const onApply = usePrivilegedPicker((state) => state.onApply);
  const { t } = useTranslation('location-picker');

  const selectedGroupIds = useMemo(
    () =>
      getSelectedGroupIdsForOrg({
        orgScopeHierarchy: scopeHierarchy[inFlightOrganizationId] || {},
        selectedLocationIds: inFlightLocationIds,
      }),
    [scopeHierarchy, inFlightOrganizationId, inFlightLocationIds]
  );

  const handleApplySelection = async () => {
    setFilterLoading(true);
    try {
      await onApply?.();

      commitPendingWorkspace();
      closeModal?.();
    } catch (error) {
      if (onApplyError) {
        onApplyError(error);
      } else {
        console.error(error);
      }
    } finally {
      setFilterLoading(false);
    }
  };

  const isWorkspacePendingSelection = pendingWorkspaceId !== null;
  const hasManualLocations = inFlightLocationIds.length > 0;
  const hasSingleParentManualSelection = selectedGroupIds.length === 1;
  const isApplyDisabled =
    !hasSwitchedScope ||
    !inFlightOrganizationId ||
    (isWorkspacePendingSelection ? !hasManualLocations : !hasManualLocations || !hasSingleParentManualSelection);

  return (
    <div className={footerStyle}>
      <InFlightSelection />
      <div className={footerActionsStyle}>
        {!isSimpleView && (
          <Button variant='secondary' size='large' onClick={closeModal}>
            {t('Cancel')}
          </Button>
        )}
        <Button disabled={isApplyDisabled} loading={filterLoading} size='large' onClick={handleApplySelection}>
          {t('Apply')}
        </Button>
      </div>
    </div>
  );
};

export const PrivilegedLocationPicker = () => {
  const isSimpleView = useLocationPicker((state) => state.isSimpleView);

  return (
    <>
      {!isSimpleView && <CurrentlyAppliedScopeWrapper />}
      <LocationPickerHeaderWrapper />
      <LocationPickerBody />
      <LocationPickerBanners />
      <LocationPickerFooter />
    </>
  );
};

export const LocationPickerBanners = () => {
  const renderBannerInfo = usePrivilegedPicker((state) => state.renderBannerInfo);

  return renderBannerInfo?.();
};

const CurrentlyAppliedScopeWrapper = () => {
  const selectedLocationIds = usePrivilegedPicker((state) => state.selectedLocationIds);
  const getScopeName = usePrivilegedPicker((state) => state.getScopeName);
  const selectedOrgId = usePrivilegedPicker((state) => state.selectedOrgId);
  const selectedGroupId = usePrivilegedPicker((state) => state.selectedGroupId);
  const locationNames = usePrivilegedPicker((state) => state.getLocationNames(selectedLocationIds));

  return (
    <CurrentlyAppliedScope
      organizationName={getScopeName(selectedOrgId, 'org')}
      groupName={getScopeName(selectedGroupId, 'group')}
      locationName={locationNames}
    />
  );
};

const LocationPickerHeaderWrapper = () => {
  const searchResultCount = usePrivilegedPicker((state) => state.searchResultCount);
  const orgIds = usePrivilegedPicker((state) => state.selectedOrgId);
  const getScopeName = usePrivilegedPicker((state) => state.getScopeName);
  const renderOrgInfo = usePrivilegedPicker((state) => state.renderOrgInfo);

  return (
    <LocationPickerHeader
      count={searchResultCount}
      organizationIds={[orgIds]}
      getScopeName={getScopeName}
      renderOrgInfo={renderOrgInfo}
    />
  );
};

const footerStyle = css`
  padding: ${theme.spacing(2)};
  gap: ${theme.spacing(2)};
  border-top: 1px solid ${theme.colors.neutral20};
`;

const footerActionsStyle = css`
  display: flex;
  justify-content: flex-end;
  gap: ${theme.spacing(2)};
`;

const containerBaseStyle = css`
  overflow: hidden;
`;

const textAlignLeftStyle = css`
  text-align: left;
`;

const textAlignRightStyle = css`
  text-align: right;
`;

const containerStyle = (alignText: 'left' | 'right' = 'left') =>
  cx(containerBaseStyle, alignText === 'right' ? textAlignRightStyle : textAlignLeftStyle);

const breadcrumbsContainerBaseStyle = css`
  display: flex;
  align-items: center;
  width: 100%;
  gap: ${theme.spacing(1)};
  max-width: 400px;
  margin-bottom: ${theme.spacing(3)};
`;

const breadcrumbsRightStyle = css`
  justify-content: flex-end;
  margin-left: auto;
`;

const breadcrumbsLeftStyle = css`
  justify-content: flex-start;
`;

const breadcrumbsContainerStyle = (alignText: 'left' | 'right' = 'left') =>
  cx(breadcrumbsContainerBaseStyle, alignText === 'right' ? breadcrumbsRightStyle : breadcrumbsLeftStyle);

const currentlyAppliedScopeStyle = css`
  margin-left: ${theme.spacing(3)};
`;

const emptyStateGraphicStyle = css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(2)};
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: ${theme.spacing(3, 5)};
`;

const emptyStateImageStyle = css`
  max-width: 300px;
  max-height: 300px;
`;

const emptyStateTextStyle = css`
  width: 65%;
`;

const locationGroupPaddingStyle = css`
  padding: ${theme.spacing(1, 2)};
`;

export const InFlightSelection = () => {
  const { t } = useTranslation('location-picker');
  const getScopeName = usePrivilegedPicker((state) => state.getScopeName);
  const { inFlightOrganizationId, inFlightGroupId, inFlightLocationIds } = useLocationPicker([
    'inFlightOrganizationId',
    'inFlightGroupId',
    'inFlightLocationIds',
  ]);

  const hasSwitchedScope = usePrivilegedPicker((state) => state.hasSwitchedScope);

  const locationNames = usePrivilegedPicker((state) => state.getLocationNames(inFlightLocationIds));

  return (
    <AnimatePresence>
      {hasSwitchedScope ? (
        <motion.div
          initial={{ height: 0 }}
          animate={{ height: 'auto' }}
          exit={{ height: 0 }}
          className={containerStyle('right')}
        >
          <LocationBreadcrumbDisplay
            label={t('Switching to:')}
            organizationName={getScopeName(inFlightOrganizationId, 'org')}
            groupName={getScopeName(inFlightGroupId, 'group')}
            locationName={locationNames}
            alignText='right'
          />
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

const LocationBreadcrumbDisplay = ({
  label,
  organizationName,
  groupName,
  locationName,
  alignText = 'left',
}: {
  label: string;
  organizationName: string;
  groupName: string;
  locationName: string;
  alignText?: 'left' | 'right';
}) => {
  return (
    <>
      <Text size='large' color='subdued'>
        {label}
      </Text>
      <div className={breadcrumbsContainerStyle(alignText)}>
        <LocationBreadcrumb>{organizationName || '...'}</LocationBreadcrumb>
        <Icon name='caret-right-small' />
        <LocationBreadcrumb>{groupName || '...'}</LocationBreadcrumb>
        <Icon name='caret-right-small' />
        <LocationBreadcrumb>{locationName || '...'}</LocationBreadcrumb>
      </div>
    </>
  );
};

const CurrentlyAppliedScope = ({
  organizationName,
  groupName,
  locationName,
}: {
  organizationName: string;
  groupName: string;
  locationName: string;
}) => {
  const { t } = useTranslation('location-picker');
  if (!organizationName && !groupName && !locationName) {
    return null;
  }

  return (
    <div className={cx(containerStyle(), currentlyAppliedScopeStyle)}>
      <LocationBreadcrumbDisplay
        label={t('Currently Applied:')}
        organizationName={organizationName}
        groupName={groupName}
        locationName={locationName}
      />
    </div>
  );
};

InFlightSelection.displayName = 'InFlightSelection';

const breadcrumbStyle = css`
  color: ${theme.font.colors.default};
  flex: 0 1 auto !important;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline;
  text-align: start;
  width: auto;
`;

const LocationBreadcrumb = ({ children }: { children: React.ReactNode }) => (
  <Truncated as={Text} size='large' className={breadcrumbStyle}>
    {children}
  </Truncated>
);

const EmptyState = emptyStateGraphics.search;

const EmptyStateGraphic = () => {
  return (
    <div className={emptyStateGraphicStyle}>
      <EmptyState className={emptyStateImageStyle} />
      <Text className={emptyStateTextStyle} textAlign='center' color='light' size='large'>
        Start searching Organizations, Location Groups, or Locations using the search bar above.
      </Text>
    </div>
  );
};

// Stable empty array to avoid creating a new reference on every render
// when isSwitchingOrg || isSearchMode is true.
const EMPTY_LOCATION_IDS: string[] = [];

const WorkspaceSection = () => {
  const { isSearchMode, inFlightOrganizationId, inFlightLocationIds, organizationViewId, isSimpleView } =
    useLocationPicker([
      'isSearchMode',
      'inFlightOrganizationId',
      'inFlightLocationIds',
      'organizationViewId',
      'isSimpleView',
    ]);
  const { setInFlightLocationIds } = useLocationPicker(['setInFlightLocationIds']);
  const selectedOrgId = usePrivilegedPicker((state) => state.selectedOrgId);
  const scopeHierarchy = usePrivilegedPicker((state) => state.scopeHierarchy);
  const { pendingWorkspaceId, setPendingWorkspaceId } = useWorkspaceShallowStore(
    'pendingWorkspaceId',
    'setPendingWorkspaceId'
  );
  const isSwitchingOrg = hasPendingOrgSwitch({
    selectedOrgId,
    organizationViewId,
    inFlightOrganizationId,
  });
  const workspaceOrgId = organizationViewId || inFlightOrganizationId || selectedOrgId;

  const allLocationIds = useMemo(
    () =>
      isSwitchingOrg || isSearchMode
        ? EMPTY_LOCATION_IDS
        : Array.from(new Set(Object.values(scopeHierarchy[workspaceOrgId] || {}).flat())),
    [isSearchMode, isSwitchingOrg, scopeHierarchy, workspaceOrgId]
  );
  const { shouldShowWorkspaces } = useWorkspaceState(workspaceOrgId);
  const showWorkspaces = !isSwitchingOrg && !isSearchMode && shouldShowWorkspaces && !isSimpleView;

  // Stable callback reference — prevents GlsWorkspaceSection from re-rendering
  // due to a new function reference on every parent render.
  const handleSelectWorkspace = useCallback(
    (locationIds: string[]) => setInFlightLocationIds(locationIds),
    [setInFlightLocationIds]
  );

  useEffect(() => {
    if (isSwitchingOrg && pendingWorkspaceId !== null) {
      setPendingWorkspaceId(null);
    }
  }, [isSwitchingOrg, pendingWorkspaceId]);

  if (!showWorkspaces) {
    return null;
  }

  return (
    <GlsWorkspaceSection
      orgId={workspaceOrgId}
      accessibleLocationIds={allLocationIds}
      selectedLocationIds={inFlightLocationIds}
      onSelectWorkspace={handleSelectWorkspace}
    />
  );
};

/**
 * Lightweight gate that prevents WorkspaceSection from mounting
 * (and running when the workspace feature is disabled) until the wetag has been loaded.
 */
const WorkspaceSectionGate = () => {
  const [isEnabled, isLoading] = GlsOrg.useWetag(WORKSPACE_TAG);
  if (isLoading || !isEnabled) return null;
  return <WorkspaceSection />;
};

const LocationPickerBody = () => {
  const { isSearchMode } = useLocationPicker(['isSearchMode']);

  return (
    <div style={{ overflow: 'auto', flex: 1 }}>
      <WorkspaceSectionGate />
      {!isSearchMode ? <LocationGroupsDisplay /> : <SearchResultsDisplay />}
    </div>
  );
};

const SearchResultsDisplay = () => {
  const { organizationViewId, inFlightOrganizationId } = useLocationPicker([
    'organizationViewId',
    'inFlightOrganizationId',
  ]);
  const selectedOrgId = usePrivilegedPicker((state) => state.selectedOrgId);
  const isSwitchingOrg = hasPendingOrgSwitch({
    selectedOrgId,
    organizationViewId,
    inFlightOrganizationId,
  });
  const { setInFlightLocationIds } = useLocationPicker(['setInFlightLocationIds']);
  const searchResults = usePrivilegedPicker((state) => state.searchResults);
  const orgLocationGroups = usePrivilegedPicker((state) => state.orgLocationGroups);
  const scopeHierarchy = usePrivilegedPicker((state) => state.scopeHierarchy);
  const getScopeName = usePrivilegedPicker((state) => state.getScopeName);
  const hasSearchResults = usePrivilegedPicker((state) => state.hasSearchResults);
  const selectedScope = useSelectedScope();
  const { pendingWorkspaceId } = useWorkspaceShallowStore('pendingWorkspaceId');
  // Allow cross-group selection when in workspace mode OR when a workspace is pending selection
  const allowCrossGroupSelection =
    !isSwitchingOrg && (selectedScope?.scopeType === 'workspace' || pendingWorkspaceId !== null);

  if (!hasSearchResults) {
    return <EmptyStateGraphic />;
  }

  return (
    <>
      {searchResults.locationGroups.length > 0 && (
        <LocationSearchGroup id='locations'>
          {searchResults.locationGroups.map((group) => (
            <LocationSearchResults
              key={group.groupId}
              groupResults={group}
              allowCrossGroupSelection={allowCrossGroupSelection}
            />
          ))}
        </LocationSearchGroup>
      )}

      {searchResults.groups.length > 0 && (
        <LocationSearchGroup id='locationGroups'>
          {searchResults.groups.map((group) => (
            <GroupSearchResults
              key={group.id}
              organizationName={group.orgName}
              organizationId={group.orgId}
              locationGroupId={group.id}
              locationGroupName={group.name}
              numberOfLocations={group.locationCount || 0}
              locations={scopeHierarchy[group.orgId]?.[group.id] || []}
              getScopeName={getScopeName}
              allowCrossGroupSelection={allowCrossGroupSelection}
              onSelected={() => {
                setInFlightLocationIds(scopeHierarchy[group.orgId][group.id]);
              }}
            />
          ))}
        </LocationSearchGroup>
      )}

      {searchResults.organizations.length > 0 && (
        <LocationSearchGroup id='organizations'>
          {searchResults.organizations.map((org) => (
            <OrgSearchResults
              key={org.id}
              organizationName={org.name}
              organizationId={org.id}
              numberOfGroups={org.groupCount || 0}
              numberOfLocations={org.locationCount || 0}
              locationGroups={orgLocationGroups[org.id] || []}
              getScopeName={getScopeName}
              allowCrossGroupSelection={allowCrossGroupSelection}
            />
          ))}
        </LocationSearchGroup>
      )}
    </>
  );
};

const LocationGroupsDisplay = () => {
  const { organizationViewId, inFlightOrganizationId } = useLocationPicker([
    'organizationViewId',
    'inFlightOrganizationId',
  ]);
  const selectedOrgId = usePrivilegedPicker((state) => state.selectedOrgId);
  const isSwitchingOrg = hasPendingOrgSwitch({
    selectedOrgId,
    organizationViewId,
    inFlightOrganizationId,
  });

  const currentOrgLocationGroups = usePrivilegedPicker((state) => state.currentOrgLocationGroups);
  const currentOrgData = usePrivilegedPicker((state) => state.currentOrgData);
  const getScopeName = usePrivilegedPicker((state) => state.getScopeName);
  const renderLocationInfo = usePrivilegedPicker((state) => state.renderLocationInfo);
  const selectedScope = useSelectedScope();
  const { pendingWorkspaceId } = useWorkspaceShallowStore('pendingWorkspaceId');
  // Allow cross-group selection when in workspace mode OR when a workspace is pending selection
  const allowCrossGroupSelection =
    !isSwitchingOrg && (selectedScope?.scopeType === 'workspace' || pendingWorkspaceId !== null);

  if (!currentOrgLocationGroups || currentOrgLocationGroups.length === 0) {
    return <EmptyStateGraphic />;
  }

  return currentOrgLocationGroups.length > 1 ? (
    <LocationGroup key={organizationViewId}>
      {currentOrgLocationGroups.map((locationGroup) => (
        <LocationGroupSection
          key={locationGroup}
          groupId={locationGroup}
          groupLocations={currentOrgData[locationGroup] || []}
          getScopeName={getScopeName}
          renderLocationInfo={renderLocationInfo}
          allowCrossGroupSelection={allowCrossGroupSelection}
        >
          {getScopeName(locationGroup, 'group')}
        </LocationGroupSection>
      ))}
    </LocationGroup>
  ) : (
    <div className={locationGroupPaddingStyle}>
      <LocationGroupSectionSimpleView
        currentOrgId={selectedOrgId}
        groupId={currentOrgLocationGroups[0]}
        groupLocations={currentOrgData[currentOrgLocationGroups[0]] || []}
        getScopeName={getScopeName}
        renderLocationInfo={renderLocationInfo}
        allowCrossGroupSelection={allowCrossGroupSelection}
      />
    </div>
  );
};
